import type { System } from '@/core/system';
import type { Engine } from '@/core/engine';
import type { Time } from '@/core/time';
import { AI, PLAYER } from '@/config';
import { clamp01, remap, damp } from '@/utils/math';
import {
  emptyPreferences,
  type PreferenceVector,
  type PlayerTelemetry,
} from '@/core/types';
import { BehaviorMemory, type BehaviorSnapshot } from './behavior-memory';

/**
 * The inference layer: it watches the player's behavior and *guesses* what they
 * like. This is a Utility-AI style scorer — each preference axis is an independent
 * utility function over the sensed signals, deliberately hand-authored and legible
 * rather than a black box, so the city's guesses feel like a personality rather than
 * noise.
 *
 * Two properties make it feel alive:
 *   - It publishes a *smoothed* estimate, so the city seems to "make up its mind"
 *     gradually rather than twitching frame to frame.
 *   - Because the underlying memory fades, sustained changes in play style will,
 *     over minutes, pull the estimate — and therefore the city — in a new direction.
 */
export class PlayerAnalyzer implements System {
  readonly name = 'analyzer';

  readonly memory = new BehaviorMemory();
  private preferences: PreferenceVector = emptyPreferences();
  private timer = 0;
  private lastSnapshot: BehaviorSnapshot | null = null;

  constructor(private engine: Engine) {}

  init(): void {
    this.engine.events.on('player:telemetry', this.onTelemetry);
  }

  private onTelemetry = (t: PlayerTelemetry): void => {
    // Only remember behavior once the player has actually entered the world.
    if (!this.engine.hasEntered) return;
    this.memory.ingest(t);
  };

  update(time: Time): void {
    if (!this.engine.hasEntered) return;
    this.timer += time.delta;
    if (this.timer < AI.analyzeInterval) return;
    this.timer = 0;

    const s = this.memory.snapshot();
    this.lastSnapshot = s;
    const raw = this.infer(s);

    // Ease the published estimate toward the raw inference.
    const l = AI.preferenceLambda;
    const dt = AI.analyzeInterval;
    for (const k of Object.keys(raw) as (keyof PreferenceVector)[]) {
      this.preferences[k] = damp(this.preferences[k], raw[k], l, dt);
    }
    this.engine.events.emit('ai:preferences', { ...this.preferences });
  }

  /** Map sensed behavior to inferred affinities. Each output is in [0,1]. */
  private infer(s: BehaviorSnapshot): PreferenceVector {
    // The city reads you quickly — a few seconds of movement is enough to commit,
    // so the world starts responding almost as soon as you do.
    const confidence = clamp01(s.age / 6);

    const velocity = remap(s.avgSpeed, PLAYER.walkSpeed * 0.5, PLAYER.sprintSpeed, 0, 1);

    // Up-gaze matters: in a young, low city the player *cannot* be elevated yet,
    // so longing for height must be readable from where they point their eyes.
    const verticality = clamp01(
      s.elevatedFrac * 0.65 + remap(s.avgHeight, 1, 18, 0, 1) * 0.5 + s.upGazeFrac * 0.6,
    );

    const parkour = clamp01(remap(s.jumpRate, 0.02, 0.55, 0, 1) * 0.7 + s.elevatedFrac * 0.35);

    const intimacy = clamp01((1 - s.openness) * 1.15) * clamp01(s.activity + 0.3);

    const contemplation = clamp01(s.gazeFrac * 1.25 + (1 - s.activity) * 0.35);

    const exploration = clamp01(
      remap(s.noveltyRate, 0.03, 0.5, 0, 1) * 0.8 + clamp01(s.activity) * 0.2,
    );

    const transgression = 0; // reserved for a future crime/forbidden-space mechanic

    const scale = (v: number) => v * confidence;
    return {
      velocity: scale(velocity),
      verticality: scale(verticality),
      parkour: scale(parkour),
      intimacy: scale(intimacy),
      contemplation: scale(contemplation),
      exploration: scale(exploration),
      transgression: scale(transgression),
    };
  }

  getPreferences(): PreferenceVector {
    return this.preferences;
  }
  getSnapshot(): BehaviorSnapshot | null {
    return this.lastSnapshot;
  }
}
